/* eslint-disable max-lines */
import {
  type AttendanceOnDemandIntegrationConfig,
  type UkgIntegrationConfig,
} from "@clipboard-health/contract-backend-main";
import { isNil } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { ClockOutChartingReminder } from "@src/appV2/redesign/Charting/ClockOutChartingReminder";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  SHIFT_DETAILS_FULL_PATH,
  SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import type { InternalTimeClockTutorialStep } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { InternalTimeClockTutorialBottomSheet } from "../TimekeepingIntegrations/InternalTimeClockTutorialBottomSheet";
import { UseInternalTimeClockContent } from "../TimekeepingIntegrations/UseInternalTimeClockContent";
import { ActionNotFoundContent } from "./ActionNotFoundContent";
import { ActionSkippedContent } from "./ActionSkippedContent";
import {
  DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS,
  TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
} from "./constants";
import { ExtendedRetryLoadingContent } from "./ExtendedRetryLoadingContent";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { useExtendedTimekeepingRetry } from "./useExtendedTimekeepingRetry";

export interface ClockOutTimekeepingIntegrationBottomSheetProps {
  modalState: UseModalState;
  config: UkgIntegrationConfig | AttendanceOnDemandIntegrationConfig;
  workerCredentials: {
    badgeNumber: string;
  };
  shiftId: string;
  workplaceId: string;
  workerId: string;
  workplaceName: string;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
  onTimekeepingActionFailed: (props: { error: string }) => void;
}

type ClockOutTimekeepingIntegrationBottomSheetStep =
  | "charting-reminder"
  | "use-time-clock"
  | "action-not-found"
  | "action-skipped"
  | "requesting-clock-out"
  | "requesting-non-instant-pay"
  | "clock-out-success";

export function ClockOutTimekeepingIntegrationBottomSheet(
  props: ClockOutTimekeepingIntegrationBottomSheetProps
) {
  const {
    modalState,
    config,
    workerCredentials,
    shiftId,
    workplaceId,
    workplaceName,
    workerId,
    onTimekeepingActionCompleted,
    onTimekeepingActionFailed,
  } = props;

  const history = useHistory();
  const queryClient = useQueryClient();
  const chartingReminderModalState = useModalState();
  const internalTimeClockTutorialModalState = useModalState();

  const { chartingRecallEnabled: shouldShowChartingReminder } =
    useGetShiftFeedbackClockOutConfig(workplaceId);

  function getFirstStep(): ClockOutTimekeepingIntegrationBottomSheetStep {
    return shouldShowChartingReminder ? "charting-reminder" : "use-time-clock";
  }

  const firstStep = getFirstStep();

  const [step, setStep] = useState<ClockOutTimekeepingIntegrationBottomSheetStep>(firstStep);

  useEffect(() => {
    if (shouldShowChartingReminder) {
      setStep((currentStep) =>
        currentStep === "use-time-clock" ? "charting-reminder" : currentStep
      );
    }
  }, [shouldShowChartingReminder]);

  useEffect(() => {
    if (modalState.modalIsOpen && step === "charting-reminder") {
      chartingReminderModalState.openModal();
    }
  }, [modalState.modalIsOpen, step, chartingReminderModalState]);

  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();
  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const {
    retryPhase: clockOutRetryPhase,
    executeWithRetry: executeClockOutWithRetry,
    cancel: cancelClockOutRetry,
  } = useExtendedTimekeepingRetry<Awaited<ReturnType<typeof recordTimekeepingAction>>>();

  const { showErrorToast } = useToast();

  const { wait, skip: skipWait } = useSkippableWait();

  const shiftDetailsPath = generatePath(SHIFT_DETAILS_FULL_PATH, {
    shiftId,
  });
  const submitTimesheetPath = `${shiftDetailsPath}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  const closeBottomSheet = useCallback(() => {
    skipWait();
    cancelClockOutRetry();
    modalState.closeModal();
    chartingReminderModalState.closeModal();
    setStep(firstStep);
  }, [modalState, chartingReminderModalState, skipWait, firstStep, cancelClockOutRetry]);

  const onClockOutSuccess = useCallback(async () => {
    onTimekeepingActionCompleted();
    setStep("clock-out-success");
    await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
    closeBottomSheet();
  }, [closeBottomSheet, wait, onTimekeepingActionCompleted]);

  const recordClockOut = useCallback(async () => {
    try {
      const postMutationShift = await executeClockOutWithRetry(
        async () =>
          await recordTimekeepingAction({
            shiftId,
            stage: ShiftStage.CLOCK_OUT,
            shiftActionCheck: ActionCheckType.INTEGRATION,
            verificationMethod: config.type,
          }),
        {
          shiftId,
          workerId,
          workplaceId,
          action: "clock-out",
        }
      );

      // If cancelled, result will be undefined
      if (isNil(postMutationShift)) {
        return;
      }

      await onClockOutSuccess();

      // Manually set shift as clocked out before the API GET call is made
      queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
        result
          ? {
              ...result,
              response: {
                ...result.response,
                clockInOut: postMutationShift.clockInOut,
              },
            }
          : undefined
      );

      history.replace(submitTimesheetPath, { clockedOut: true });
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.message === TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE
      ) {
        setStep("action-not-found");
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock out, please try again.";

      onTimekeepingActionFailed({ error: errorMessage });

      showErrorToast(errorMessage);
      closeBottomSheet();
    }
  }, [
    closeBottomSheet,
    config.type,
    executeClockOutWithRetry,
    history,
    onClockOutSuccess,
    onTimekeepingActionFailed,
    recordTimekeepingAction,
    shiftId,
    showErrorToast,
    submitTimesheetPath,
    queryClient,
    workerId,
    workplaceId,
  ]);

  const convertToNonInstantPay = useCallback(async () => {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.EXTERNAL_INTEGRATION_FAILURE,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: "EXTERNAL_INTEGRATION_FAILURE",
      });
      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }, [
    markNonInstant,
    shiftId,
    modalState,
    showErrorToast,
    closeBottomSheet,
    onTimekeepingActionCompleted,
  ]);

  return (
    <>
      {step === "charting-reminder" && shouldShowChartingReminder && (
        <ClockOutChartingReminder
          modalState={chartingReminderModalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onConfirmClockOut={() => {
            setStep("use-time-clock");
            chartingReminderModalState.closeModal();
          }}
          onCancel={() => {
            closeBottomSheet();
          }}
        />
      )}

      <BottomSheet modalState={modalState} canDismiss={false}>
        {step === "use-time-clock" && (
          <UseInternalTimeClockContent
            action="clock-out"
            badgeNumber={workerCredentials.badgeNumber}
            pinsToEnter={config.pinsToEnterString ?? ""}
            timeclockLocation={config.timeclockLocationString ?? ""}
            onActionConfirm={async () => {
              setStep("requesting-clock-out");
              await recordClockOut();
            }}
            onHelp={() => {
              internalTimeClockTutorialModalState.openModal();
            }}
            onCancel={closeBottomSheet}
          />
        )}
        {step === "action-not-found" && (
          <ActionNotFoundContent
            action="clock-out"
            onTryAgain={async () => {
              setStep("requesting-clock-out");
              await recordClockOut();
            }}
            onSkipAction={() => {
              setStep("action-skipped");
            }}
            onClose={closeBottomSheet}
          />
        )}
        {step === "action-skipped" && (
          <ActionSkippedContent
            action="clock-out"
            onAccept={async () => {
              setStep("requesting-non-instant-pay");
              await convertToNonInstantPay();
            }}
            onCancel={() => {
              setStep("use-time-clock");
            }}
          />
        )}
        {step === "requesting-clock-out" && clockOutRetryPhase !== "extended" && (
          <BottomSheetLoadingContent
            title="Confirming your clock out"
            description="Should take up to 30 seconds"
          />
        )}
        {step === "requesting-clock-out" && clockOutRetryPhase === "extended" && (
          <ExtendedRetryLoadingContent
            action="clock-out"
            onCancel={() => {
              cancelClockOutRetry();
              closeBottomSheet();
            }}
            onSkipAction={() => {
              setStep("action-skipped");
            }}
          />
        )}
        {step === "clock-out-success" && (
          <TimekeepingActionSuccessContent
            title="Clocked out"
            description="We hope you had a great shift!"
            onClose={closeBottomSheet}
          />
        )}
        {step === "requesting-non-instant-pay" && (
          <BottomSheetLoadingContent
            title="Converting to non-instant pay"
            description="Should only take a few seconds"
          />
        )}
      </BottomSheet>
      <InternalTimeClockTutorialBottomSheet
        modalState={internalTimeClockTutorialModalState}
        action="clock-out"
        steps={config.tutorial.stepsClockOut as InternalTimeClockTutorialStep[]}
        placeholders={{ workplaceName, badgeNumber: workerCredentials.badgeNumber }}
        onTimeclockActionPerformed={async () => {
          internalTimeClockTutorialModalState.closeModal();

          setStep("requesting-clock-out");
          await recordClockOut();
        }}
        onTimeclockNotWorking={() => {
          internalTimeClockTutorialModalState.closeModal();
          setStep("action-skipped");
        }}
      />
    </>
  );
}
/* eslint-enable max-lines */
